'use client';

import type { ProfileModuleDto } from '@up2stay/up2promo-api-client';

import { BiographyModuleDisplay } from './biography-module-display';
import { ContactInfoModuleDisplay } from './contact-info-module-display';
import { FilesModuleDisplay } from './files-module-display';
import { GoogleMapsModuleDisplay } from './google-maps-module-display';
import { ImagesModuleDisplay } from './images-module-display';
import { LinksModuleDisplay } from './links-module-display';
import { SocialsModuleDisplay } from './socials-module-display';
import { TeamMembersModuleDisplay } from './team-members-module-display';
import { VideoModuleDisplay } from './video-module-display';
import {
  resolvePublicProfileModuleChrome,
  type PublicProfileModuleChrome,
} from '../public-profile-module-copy';
interface PublicModuleRendererProps {
  module: ProfileModuleDto;
  /** Google Maps JavaScript API key for the google_maps module */
  googleMapsApiKey?: string | null;
  /** For team_members: build member profile links. */
  workspaceUsername?: string | null;
  profileBaseUrl?: string;
  /** Username of the currently displayed public profile (used to hide self in team_members). */
  currentUsername?: string;
  /** Optional: resolve relative file paths to full URLs. */
  getFileUrl?: (path: string) => string;
  chrome?: PublicProfileModuleChrome;
}

/**
 * Renders a profile module based on its type.
 * This component acts as a router that delegates to specific module display components.
 */
export function PublicModuleRenderer({
  module,
  googleMapsApiKey,
  workspaceUsername,
  profileBaseUrl = '',
  currentUsername,
  getFileUrl,
  chrome,
}: PublicModuleRendererProps) {
  const moduleChrome = chrome ?? resolvePublicProfileModuleChrome(undefined);
  // Only render enabled modules
  if (!module.enabled) {
    return null;
  }

  switch (module.type) {
    case 'biography':
      return <BiographyModuleDisplay module={module} />;
    case 'contact_info':
      return <ContactInfoModuleDisplay module={module} />;
    case 'socials':
      return <SocialsModuleDisplay module={module} />;
    case 'links':
      return <LinksModuleDisplay module={module} />;
    case 'video':
      return <VideoModuleDisplay module={module} />;
    case 'images':
      return (
        <ImagesModuleDisplay
          module={module}
          getFileUrl={getFileUrl}
          chrome={moduleChrome}
        />
      );
    case 'files':
      return (
        <FilesModuleDisplay
          module={module}
          getFileUrl={getFileUrl}
          chrome={moduleChrome}
        />
      );
    case 'google_maps':
      return (
        <GoogleMapsModuleDisplay
          module={module}
          googleMapsApiKey={googleMapsApiKey}
          chrome={moduleChrome}
        />
      );
    case 'team_members':
      return (
        <TeamMembersModuleDisplay
          module={module}
          workspaceUsername={workspaceUsername}
          profileBaseUrl={profileBaseUrl}
          currentUsername={currentUsername}
          getFileUrl={getFileUrl}
          chrome={moduleChrome}
        />
      );
    default:
      // Unknown module type - silently skip
      return null;
  }
}
